import {FilterSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/filter_section.types';
import {GetFilterResponse as BrandShopGetFiltersResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/service.types';
import {GetFiltersResponse as CategoryGetFiltersResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/filters/service.types';
import {useTrackOnCloseFilter, useTrackOnOpenFilter} from '@aboutyou/tracking';
import {
  CategoryHeader,
  ErrorBoundary,
  ProductImageType,
  useIsScrollingUp,
} from '@aboutyou/ui-components';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';
import {DESKTOP_STICKY_HEIGHT} from '../../backend-services/Header/constants';
import {useLocalizedSortingOptions} from '../Filters/FilterFlyout/helpers';
import {FilterSectionSorting} from '../Filters/InlineFilter/FilterSections/FilterSectionSorting';
import {FilterSectionViewOptions} from '../Filters/InlineFilter/FilterSections/FilterSectionViewOptions';
import {DEFAULT_SORT} from '../Filters/InlineFilter/helpers';

export interface StreamHeaderProps {
  productImageType?: ProductImageType;
  onChangeProductImageType: (productImageType: ProductImageType) => void;
  inlineFiltersRef: React.RefObject<HTMLDivElement | null>;
  offsetTop?: number;
  StreamGenderSwitch?: React.ReactNode;
}

const INLINE_FILTERS_OFFSET_BOTTOM = 410;

export const StreamHeader: FC<
  (CategoryGetFiltersResponse | BrandShopGetFiltersResponse) & StreamHeaderProps
> = ({
  title,
  seoSubline,
  filters,
  hideProductCount,
  productCount,
  productImageType,
  onChangeProductImageType,
  inlineFiltersRef,
  offsetTop = DESKTOP_STICKY_HEIGHT,
  StreamGenderSwitch,
  ...otherProps
}) => {
  const [filterBarOffsetTop, setFilterBarOffsetTop] = useState<number>(
    INLINE_FILTERS_OFFSET_BOTTOM,
  );
  const isScrollingUp = useIsScrollingUp({
    bottomLimit: filterBarOffsetTop,
  });

  useEffect(() => {
    if (inlineFiltersRef.current) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setFilterBarOffsetTop(
        inlineFiltersRef.current.offsetTop +
          inlineFiltersRef.current.scrollHeight -
          offsetTop,
      );
    }
  }, [inlineFiltersRef.current]);

  const currentCategoryName =
    title || ('category' in otherProps ? otherProps.category.name : '');

  const sortingOptions = useLocalizedSortingOptions();

  const trackOnCloseFilter = useTrackOnCloseFilter();
  const trackOnOpenFilter = useTrackOnOpenFilter();
  const trackOnCloseSort = () => trackOnCloseFilter('sort', 'filter_modal');
  const trackOnOpenSort = () => trackOnOpenFilter('sort', 'filter_modal');

  if (!currentCategoryName) {
    return null;
  }

  return (
    <StyledCategoryHeader
      categoryName={currentCategoryName}
      subtitle={seoSubline}
      productCount={hideProductCount ? undefined : productCount}
      stickyOnScrollUp={true}
      showBanner={isScrollingUp}
      centered={false}
      sidePadding={false}
      offsetTop={offsetTop}
      $isScrolledUp={isScrollingUp}
    >
      <CategoryHeaderContent>
        {StreamGenderSwitch}
        {filters.length > 0 && (
          <SortingAndViewWrapper data-testid="sortingAndViewWrapper">
            {filters.map((filter: FilterSection, i: number) => {
              if (!filter?.type?.$case) return null;

              switch (filter.type.$case) {
                case 'sortOptions':
                  return (
                    <ErrorBoundary
                      ErrorComponent={ErrorComponent}
                      key={`filter-dropdown-section-sort-${i}`}
                      renderContext="category_filter_dropdown_sort_options"
                    >
                      <FilterSectionSorting
                        sortingOptions={sortingOptions}
                        defaultSortingOptionId={DEFAULT_SORT}
                        onClose={trackOnCloseSort}
                        onOpen={trackOnOpenSort}
                      />
                    </ErrorBoundary>
                  );

                case 'viewOptions': {
                  const viewOptionsData = filter.type.viewOptions;

                  return (
                    <ErrorBoundary
                      ErrorComponent={ErrorComponent}
                      key={`filter-dropdown-section-view-${i}`}
                      renderContext="category_filter_view_options"
                    >
                      <FilterSectionViewOptions
                        data={viewOptionsData}
                        onChangeProductImageType={onChangeProductImageType}
                        productImageType={productImageType}
                      />
                    </ErrorBoundary>
                  );
                }
                default: {
                  return null;
                }
              }
            })}
          </SortingAndViewWrapper>
        )}
      </CategoryHeaderContent>
    </StyledCategoryHeader>
  );
};

const StyledCategoryHeader = styled(CategoryHeader)<{
  $isScrolledUp: boolean;
}>`
  height: auto;
  margin-bottom: 20px;
  padding: ${props =>
    props.$isScrolledUp ? '20px 0 20px 0' : '0.3rem 0 20px 0'};
`;

const CategoryHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
`;

const SortingAndViewWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  gap: 0.8rem;
`;

const ErrorComponent = () => null;
